<script setup lang="ts">
import PromoCode from '@components/PromoCode/PromoCode.vue';
import { type StepCheckout } from '@models/quiz';
import { useCheckoutProgressStore } from '@stores/checkout/checkoutProgressStore';
import { useCheckoutStore } from '@stores/checkout/checkoutStore';
import { useQuizStore } from '@stores/quiz';
import { computed, onMounted, ref, watch } from 'vue';
import { PRODUCT_TYPE } from '@/js/models/product';
import { useAnalyticsStore } from '@/js/stores';

const props = defineProps<{
  content: StepCheckout;
}>();

const emit = defineEmits<{
  'next': [];
  'back': [];
}>();

const checkoutStore = useCheckoutStore();
const checkoutProgressStore = useCheckoutProgressStore();
const quizStore = useQuizStore();
const analyticsStore = useAnalyticsStore();

const {
  topBannerText,
  topBannerPromocode,
} = quizStore;

const next = () => {
  if (checkoutProgressStore.currentStepIndex === checkoutProgressStore.steps.length - 1) {
    emit('next');
    return;
  }
  checkoutProgressStore.currentStepIndex += 1;
};

const back = () => {
  if (checkoutProgressStore.currentStepIndex === 0) {
    emit('back');
    return;
  }
  checkoutProgressStore.currentStepIndex -= 1;
};

// Set store products
checkoutStore.products = props.content.products;

// Set the store's main product
checkoutStore.mainProduct = props.content.products
  .find((product) => product.product.id === quizStore.prefillProductId)
    || props.content.products[0];

// Filter the selected product and membership
const filteredProducts = computed(() => {
  return props.content.products.filter((p) => {
    return p.product.id !== checkoutStore.mainProduct?.product.id
      && p.product.productType.includes(PRODUCT_TYPE.INJECTION);
  });
});

const otherProducts = computed(() => {
  return props.content.products.filter((p) => {
    return p.product.id !== checkoutStore.mainProduct?.product.id
        && !p.product.productType.includes(PRODUCT_TYPE.INJECTION)
        && !p.product.productType.includes(PRODUCT_TYPE.ADD_ON)
        && !p.product.productType.includes(PRODUCT_TYPE.DIGITAL);
  });
});

const isPromoCode = ref(false);

onMounted(() => {
  const searchParams = new URLSearchParams(location.search);

  if (searchParams.get('newyear') !== null) {
    isPromoCode.value = false; // todo: set to true when promo code input field will work
  }
});

const scrollToTop = () => window.scrollTo(0, 0);
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-animation">
      <PromoCode
        v-if="isPromoCode && topBannerText && topBannerPromocode"
        :text="topBannerText"
        :code="topBannerPromocode"
      />
    </Transition>
  </Teleport>

  <div class="px-20 text-black lg:px-40">
    <Transition
      name="fade"
      mode="out-in"
      @after-leave="scrollToTop"
    >
      <component
        :is="checkoutProgressStore.currentStep"
        :content="content"
        :product-list="filteredProducts"
        :other-product-list="otherProducts"
        @next="next"
        @back="back"
      />
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
// Fade animation
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
.checkout-slide-left {
  animation: slide-left 0.5s both;
}

@keyframes slide-left {
  from {
    transform: translateX(120px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
